import {
    getPrintFileUploadUrl,
    PrintFileApiError,
    PrintFileReviewResult,
    savePrintFileReview,
    setPrintFileBatchStatus,
    uploadPrintFileToS3,
} from '../../api/printFileBatches';
import { downloadBatchAssets, getStoredBlob } from './assets';
import { dbDelete, dbGet, dbGetAll, dbGetAllByIndex, dbUpdate } from './db';
import { notifyReviewStore } from './events';
import { addEditLog } from './logs';
import { IBatchStatusOp, ILocalBatch, ILocalFile, IOutboxOp, IReviewOp } from './types';

/**
 * Background sync of review results. Every server write goes through the persistent
 * outbox, so nothing is lost on reload/offline: the page just enqueues and moves on.
 * Edited files are uploaded to S3 first (presigned PUT), then the review is saved.
 */

const BACKOFF_BASE_MS = 5000;
const BACKOFF_MAX_MS = 5 * 60 * 1000;
/** After this many failed attempts a save waits for "Retry now" (5+10+20+40+80s of backoff ≈ 2.5 min). */
export const MAX_AUTO_ATTEMPTS = 6;
/** 400/404 won't fix themselves — wait for a manual retry. */
const PARKED = Number.MAX_SAFE_INTEGER;

const RETRIES_STOPPED_KEY = 'printFileReview.retriesStopped';

let running = false;
let timer: ReturnType<typeof setTimeout> | null = null;

/** Designer switched automatic retries off: failed requests wait for a manual retry. */
export const areRetriesStopped = () => {
    try {
        return localStorage.getItem(RETRIES_STOPPED_KEY) === '1';
    } catch (e) {
        return false;
    }
};

const withLock = async (name: string, fn: () => Promise<void>) => {
    const locks = (navigator as any).locks;
    if (!locks?.request) return fn();
    // Only one tab syncs at a time; the others leave the outbox to it.
    return locks.request(name, { ifAvailable: true }, async (lock: unknown) => {
        if (lock) await fn();
    });
};

const setFileSync = (fileId: string, sync: Partial<ILocalFile['sync']>, extra?: Partial<ILocalFile>) =>
    dbUpdate<ILocalFile>('files', fileId, (f) => (f ? { ...f, ...extra, sync: { ...f.sync, ...sync } } : undefined));

const isCurrent = (op: IOutboxOp) => (cur: IOutboxOp | undefined) => !!cur && cur.version === op.version;

/**
 * `presetFinalUrl` — a final that is already on the server (the upscaled draft, or an edit
 * uploaded from another browser): saved as a manual edit with that URL, nothing is uploaded.
 */
export const enqueueReview = async (
    file: ILocalFile,
    reviewResult: PrintFileReviewResult,
    hasManualEdits: boolean,
    presetFinalUrl?: string | null
) => {
    const key = `review:${file.id}`;
    await dbUpdate<IOutboxOp>('outbox', key, (cur) => ({
        key,
        kind: 'review',
        fileId: file.id,
        batchId: file.batchId,
        version: (cur?.version ?? 0) + 1,
        reviewResult,
        hasManualEdits,
        presetFinalUrl: hasManualEdits && presetFinalUrl ? presetFinalUrl : undefined,
        createdAt: cur?.createdAt ?? Date.now(),
        attempts: 0,
        nextAttemptAt: 0,
    }));
    await setFileSync(file.id, { state: 'queued', error: undefined, parked: undefined });
    notifyReviewStore();
    kickSync();
};

export const enqueueBatchStatus = async (batchId: string, status: 'in_progress' | 'done') => {
    const batch = await dbGet<ILocalBatch>('batches', batchId);
    if (!batch) return;
    if (status === 'in_progress' && batch.serverStatus !== 'init') return;
    if (status === 'done' && batch.serverStatus === 'done') return;
    const key = `batch:${batchId}`;
    await dbUpdate<IOutboxOp>('outbox', key, (cur) => {
        // "done" already queued wins over a late "in_progress".
        if (status === 'in_progress' && cur?.kind === 'batchStatus' && cur.status === 'done') return undefined;
        return {
            key,
            kind: 'batchStatus',
            batchId,
            version: (cur?.version ?? 0) + 1,
            status,
            createdAt: cur?.createdAt ?? Date.now(),
            attempts: 0,
            nextAttemptAt: 0,
        };
    });
    notifyReviewStore();
    kickSync();
};

/** Mark the batch done on the server once every file's review is saved there. */
const maybeFinishBatch = async (batchId: string) => {
    const files = await dbGetAllByIndex<ILocalFile>('files', 'batchId', batchId);
    if (files.length && files.every((f) => f.decision && f.sync.state === 'synced')) {
        await enqueueBatchStatus(batchId, 'done');
    }
};

const failOp = async (op: IOutboxOp, e: PrintFileApiError) => {
    const status = e?.status;
    const message = e?.message || 'Request failed';
    const attempts = op.attempts + 1;
    const permanent = status === 400 || status === 404;
    const stopped = !permanent && areRetriesStopped();
    const limit = !permanent && !stopped && attempts >= MAX_AUTO_ATTEMPTS;
    const parked = permanent ? 'permanent' : stopped ? 'stopped' : limit ? 'limit' : undefined;
    const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (attempts - 1));
    await dbUpdate<IOutboxOp>('outbox', op.key, (cur) =>
        isCurrent(op)(cur)
            ? { ...cur!, attempts, lastError: message, retryStopped: stopped, nextAttemptAt: parked ? PARKED : Date.now() + delay }
            : undefined
    );
    if (op.kind === 'review') await setFileSync(op.fileId, { state: 'error', error: message, parked });
    await addEditLog({
        batchId: op.batchId,
        fileId: op.kind === 'review' ? op.fileId : undefined,
        action: 'sync_error',
        details: {
            op: op.kind,
            status,
            message,
            attempts,
            retry: permanent ? 'manual' : stopped ? 'stopped' : limit ? `limit (${MAX_AUTO_ATTEMPTS} attempts)` : `${Math.round(delay / 1000)}s`,
        },
    });
};

const processReview = async (op: IReviewOp) => {
    const file = await dbGet<ILocalFile>('files', op.fileId);
    if (!file) {
        await dbDelete('outbox', op.key);
        return;
    }
    let finalUrl = op.finalUrl ?? op.presetFinalUrl;
    if (op.hasManualEdits && !finalUrl) {
        await setFileSync(op.fileId, { state: 'uploading', error: undefined, parked: undefined });
        notifyReviewStore();
        const blob = await getStoredBlob(op.fileId, 'final');
        if (!blob) throw Object.assign(new Error('Edited file is missing on this device — edit again or revert'), { status: 400 });
        const type = blob.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const upload = await getPrintFileUploadUrl(type, op.fileId);
        await uploadPrintFileToS3(upload.signedUrl, blob, type);
        finalUrl = upload.url;
        const stored = await dbUpdate<IOutboxOp>('outbox', op.key, (cur) => (isCurrent(op)(cur) ? { ...cur!, finalUrl } : undefined));
        await addEditLog({ batchId: op.batchId, fileId: op.fileId, action: 'upload_ok', details: { url: finalUrl, key: upload.key, bytes: blob.size, type } });
        // The designer changed the decision/edit while we were uploading — the newer op takes over.
        if (!stored.changed) return;
    }
    await setFileSync(op.fileId, { state: 'saving', error: undefined, parked: undefined });
    notifyReviewStore();
    const saved = await savePrintFileReview(
        op.fileId,
        op.hasManualEdits
            ? { reviewResult: op.reviewResult, hasManualEdits: true, finalPrintReadyUrl: finalUrl! }
            : { reviewResult: op.reviewResult, hasManualEdits: false }
    );
    const cleared = await dbUpdate<IOutboxOp>('outbox', op.key, (cur) => (isCurrent(op)(cur) ? null : undefined));
    await addEditLog({
        batchId: op.batchId,
        fileId: op.fileId,
        action: 'review_saved',
        details: {
            reviewResult: op.reviewResult,
            hasManualEdits: op.hasManualEdits,
            finalPrintReadyUrl: saved?.finalPrintReadyUrl ?? finalUrl ?? null,
            superseded: !cleared.changed,
        },
    });
    if (!cleared.changed) return;
    await setFileSync(
        op.fileId,
        { state: 'synced', error: undefined, syncedAt: Date.now(), finalPrintReadyUrl: saved?.finalPrintReadyUrl ?? finalUrl ?? null },
        { printFileStatus: saved?.printFileStatus ?? 'reviewed' }
    );
    await maybeFinishBatch(op.batchId);
};

const processBatchStatus = async (op: IBatchStatusOp) => {
    await setPrintFileBatchStatus(op.batchId, op.status);
    await dbUpdate<IOutboxOp>('outbox', op.key, (cur) => (isCurrent(op)(cur) ? null : undefined));
    await dbUpdate<ILocalBatch>('batches', op.batchId, (b) =>
        b ? { ...b, serverStatus: op.status, doneSyncedAt: op.status === 'done' ? Date.now() : b.doneSyncedAt } : undefined
    );
    await addEditLog({ batchId: op.batchId, action: 'batch_status_saved', details: { status: op.status } });
    // A done pack is viewed from the server finals — bring them into this browser.
    // (Second pass: a download already running for this pack started before it was done.)
    if (op.status === 'done') void downloadBatchAssets(op.batchId).then(() => downloadBatchAssets(op.batchId));
};

const scheduleNext = async () => {
    if (timer) clearTimeout(timer);
    timer = null;
    try {
        const ops = await dbGetAll<IOutboxOp>('outbox');
        const soonest = Math.min(...ops.map((o) => o.nextAttemptAt).filter((t) => t < PARKED));
        if (Number.isFinite(soonest)) timer = setTimeout(kickSync, Math.max(1000, soonest - Date.now()));
    } catch (e) {
        /* DB unavailable — nothing to schedule */
    }
};

const runSync = async () => {
    running = true;
    try {
        await withLock('print-file-review-sync', async () => {
            // Bounded: superseded ops are picked up again, but never loop forever.
            for (let pass = 0; pass < 50; pass++) {
                const now = Date.now();
                const due = (await dbGetAll<IOutboxOp>('outbox'))
                    .filter((o) => o.nextAttemptAt <= now)
                    .sort((a, b) => a.createdAt - b.createdAt);
                if (!due.length) return;
                for (const op of due) {
                    try {
                        if (op.kind === 'review') await processReview(op);
                        else await processBatchStatus(op);
                    } catch (e: any) {
                        await failOp(op, e);
                        if (e?.status === 401) return;
                    }
                    notifyReviewStore();
                }
            }
        });
    } catch (e) {
        console.warn('Print review sync failed', e);
    } finally {
        running = false;
        notifyReviewStore();
        void scheduleNext();
    }
};

export const kickSync = () => {
    if (running) return;
    void runSync();
};

/** Retry everything now, including requests parked after a 400/404. */
export const retryAllNow = async () => {
    const ops = await dbGetAll<IOutboxOp>('outbox');
    for (const op of ops) {
        await dbUpdate<IOutboxOp>('outbox', op.key, (cur) => (cur ? { ...cur, nextAttemptAt: 0 } : undefined));
    }
    kickSync();
};

/**
 * Stop / resume automatic retries of failed requests, so a broken upload doesn't hit the
 * server every few minutes all day. New decisions are still sent once; "Retry now" still works.
 */
export const setRetriesStopped = async (stopped: boolean) => {
    try {
        if (stopped) localStorage.setItem(RETRIES_STOPPED_KEY, '1');
        else localStorage.removeItem(RETRIES_STOPPED_KEY);
    } catch (e) {
        /* private mode — the flag then only lasts for this page */
    }
    if (stopped && timer) {
        clearTimeout(timer);
        timer = null;
    }
    const ops = await dbGetAll<IOutboxOp>('outbox');
    const affected = new Set<string>();
    for (const op of ops) {
        const res = await dbUpdate<IOutboxOp>('outbox', op.key, (cur) => {
            if (!cur) return undefined;
            if (stopped && cur.lastError && cur.nextAttemptAt < PARKED) return { ...cur, retryStopped: true, nextAttemptAt: PARKED };
            if (!stopped && cur.retryStopped) return { ...cur, retryStopped: false, nextAttemptAt: 0 };
            return undefined;
        });
        if (res.changed) affected.add(op.batchId);
    }
    for (const batchId of Array.from(affected)) {
        await addEditLog({ batchId, action: stopped ? 'retries_stopped' : 'retries_resumed' });
    }
    notifyReviewStore();
    if (!stopped) kickSync();
};

if (typeof window !== 'undefined') window.addEventListener('online', kickSync);
